import type { BrandsQueriesData, BrandsQueriesName, BrandsQueriesVariables } from 'graphql/brands/types'
import { useBrandsQueries } from 'hooks-queries/brands'
import type { QueryHookParamsWithCustomOptions } from 'lib/apollo-client/types'
import { FeatureFlagsStore } from 'stores'

import type { IUseVersionedBrandsQuery, TVersionedQueryOptions } from './use-versioned-brands-query.types'

export function useVersionedBrandsQuery<TV1 extends BrandsQueriesName, TV2 extends BrandsQueriesName>({
  v1,
  v2,
  errorLog,
  variables,
  options,
}: IUseVersionedBrandsQuery<TV1, TV2>) {
  const isV2 = FeatureFlagsStore.isFeatureFlagEnabled('isNewPageBrandOverviewEnabled')
  const queryName = isV2 ? v2 : v1

  // TypeScript expands BrandsQueriesVariables[TV1] to the full union during the overlap check,
  // making a direct `as` impossible. `as unknown as` is the TypeScript-idiomatic escape hatch:
  // unlike `as any`, the result is fully type-checked as the specific target type.
  const result = useBrandsQueries({
    queryName: queryName as TV1,
    queryVariables: variables!,
    errorLog,
    options: options as TVersionedQueryOptions<TV1>,
  } as unknown as QueryHookParamsWithCustomOptions<TV1, BrandsQueriesVariables[TV1], BrandsQueriesData[TV1]>)

  return {
    ...result,
    data: result.data as BrandsQueriesData[TV1] | BrandsQueriesData[TV2] | undefined,
    isV2,
  }
}
